import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';
import { Component, OnInit, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { StorageService } from './shared/services/storage/storage.service';
import { UserService } from './shared/services/user/user.service';
import { AuthService } from './shared/services/auth/auth.service';
import { register } from 'swiper/element/bundle';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SafeArea } from '@capacitor-community/safe-area';
import { Browser } from '@capacitor/browser';

registerLocaleData(es);

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.css'],
  imports: [
    IonRouterOutlet,
    IonApp
  ]
})

export class AppComponent implements OnInit {
  constructor(
    private storage: StorageService,
    private userService: UserService,
    private authService: AuthService,
    private zone: NgZone,
    private router: Router
  ) {
    register();

    if (Capacitor.isNativePlatform()) {

      SafeArea.enable({ config: {} });

      StatusBar.setStyle({
        style: Style.Dark
      });
    } else {
      if (location.hostname !== 'localhost' && !location.hostname.includes('cycling365.cc')) {
        location.replace('https://app.dev.cycling365.cc' + location.pathname + location.search + location.hash);
      }
    }

    if (Capacitor.getPlatform() === 'android') {
      StatusBar.setOverlaysWebView({ overlay: true });
    }

    this.setupOnUrlOpenListener();
  }

  async ngOnInit(): Promise<void> {
    // Storage init
    await this.storage.init();

    // Auth init
    await this.authService.init();

    const isAuthenticated = this.authService.$isAuthenticated();

    if(isAuthenticated) {
      this.userService.getUser()
        .subscribe({
          next: (user) => this.userService.saveUser(user),
          error: (err) => {
          }
        })
    }
  }

  private setupOnUrlOpenListener() {
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      console.log('App opened with URL:', JSON.stringify(event));
      
      try {
        const url = new URL(event.url);
        const protocol = url.protocol;    // ej. "outdoors365:" o "https:"
        const hostname = url.hostname;    // ej. "ruta-destino" o "app.cycling365.cc"
        const pathname = url.pathname;    // ej. "/" o "/con/subruta"
        const search   = url.search;      // ej. "?param=xxx"

        // 1) Si viene por el esquema custom "outdoors365://..."
        if (protocol === 'outdoors365:') {
          // --- Reconstruir el “path real” dentro de Angular: ---
          //
          //   Como new URL("outdoors365://ruta-destino?param=xxx") lee
          //   hostname="ruta-destino", pathname="/" → necesitamos
          //   combinar hostname + pathname para formar "/ruta-destino"
          //
          //   Si viniera algo como "outdoors365://host/con/subruta?foo=1"
          //   entonces hostname="host", pathname="/con/subruta", y obtenemos
          //   "/host/con/subruta". A partir de eso, Angular Router interpretará
          //   correctamente la ruta.
          //
          // Reconstruimos:
          let pathFromOutdoors = '/' + hostname;       // ej. "/ruta-destino" o "/host"
          if (pathname && pathname !== '/') {
            // si el usuario pasó un subpath, lo anexamos (ej. "/con/subruta")
            pathFromOutdoors += pathname;             
          }
          // Ahora pathFromOutdoors puede ser "/ruta-destino" o "/host/con/subruta"

          // Le metemos también los query params:
          const slug = pathFromOutdoors + search;      // ej. "/ruta-destino?param=xxx"

          console.log('Esquema outdoors365:// → slug a navegar:', slug);

          // Si abriste la web con Capacitor Browser/SafariView, ciérralo:
          await Browser.close();

          // Lanzamos la navegación en Angular:
          this.router.navigateByUrl(slug);
          return;
        }

        // 2) Si viene por un dominio que termine en ".cc"
        if (hostname.endsWith('.cc')) {
          // En este caso, url.pathname ya contendrá algo como "/ruta-destino"
          // tal y como queramos, así que simplemente concatenamos:
          const slug = pathname + search; // ej. "/ruta-destino?param=xxx"
          console.log('Dominio *.cc → slug a navegar:', slug);

          await Browser.close();
          this.router.navigateByUrl(slug);
          return;
        }

        // 3) En cualquier otro caso, ignoramos o redirigimos a la home
        console.warn(`URL recibida no coincide ni con outdoors365:// ni con *.cc:`, event.url);

      } catch (err) {
        console.error('No se pudo parsear la URL:', event.url, err);
      }
    });
  }

}
